import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import {
  Trophy, Wallet, TrendingUp, CreditCard, Loader2, Sparkles,
  CheckCircle2, Lightbulb, ArrowUpRight, RotateCcw, Share2, Download, Printer, Scale,
} from "lucide-react";
import { Button } from "../ui/button";
import { fmt, fmtDelta, CATEGORIES } from "../../lib/game";
import { Handout } from "./Handout";
import { CommitmentCapture } from "./CommitmentCapture";

const Breakdown = ({ session }) => {
  const items = [
    { label: "Cash", v: session.cash, Icon: Wallet, color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/30" },
    { label: "Investments", v: session.investments, Icon: TrendingUp, color: "text-purple-400", bg: "bg-purple-500/10 border-purple-500/30" },
    { label: "Debts", v: session.debts, Icon: CreditCard, color: "text-red-400", bg: "bg-red-500/10 border-red-500/30" },
  ];
  return (
    <div className="grid grid-cols-3 gap-3">
      {items.map(({ label, v, Icon, color, bg }) => (
        <div key={label} className={`rounded-2xl border ${bg} px-4 py-3`}>
          <Icon className={`w-4 h-4 ${color} mb-1.5`} />
          <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
          <p className="font-mono text-lg font-bold text-slate-100">{fmt(v)}</p>
        </div>
      ))}
    </div>
  );
};

const Timeline = ({ decisions }) => (
  <div className="space-y-2" data-testid="decision-timeline">
    {decisions.map((d) => {
      const meta = CATEGORIES[d.category] || CATEGORIES.SAVE;
      const Icon = meta.Icon;
      const fi = d.financial_impact || {};
      const parts = [];
      if (fi.cash) parts.push({ l: "cash", v: fi.cash });
      if (fi.investments) parts.push({ l: "inv", v: fi.investments });
      if (fi.debts) parts.push({ l: "debt", v: fi.debts });
      return (
        <div key={d.id} className="flex items-start gap-3 rounded-xl border border-slate-800 bg-slate-900/50 px-3 py-2.5">
          <div className={`w-8 h-8 shrink-0 rounded-lg flex items-center justify-center ${meta.bg} border ${meta.border}`}>
            <Icon className={`w-4 h-4 ${meta.text}`} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-mono text-slate-500">R{d.round_number}</span>
              <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${meta.badge}`}>
                {meta.label}
              </span>
            </div>
            <p className="text-sm text-slate-200 mt-0.5">{d.choice_selected}</p>
          </div>
          <div className="text-right shrink-0">
            {parts.map((p) => {
              const good = p.l === "debt" ? p.v < 0 : p.v > 0;
              return (
                <p key={p.l} className={`text-[11px] font-mono font-semibold ${good ? "text-emerald-400" : "text-red-400"}`}>
                  {fmtDelta(p.v)} {p.l}
                </p>
              );
            })}
            {parts.length === 0 && <p className="text-[11px] font-mono text-slate-500">no change</p>}
          </div>
        </div>
      );
    })}
  </div>
);

const ReviewCard = ({ review, loading }) => {
  const [typed, setTyped] = useState("");
  const [showRest, setShowRest] = useState(false);

  useEffect(() => {
    if (!review || loading) return;
    setTyped("");
    setShowRest(false);
    const full = review.summary || "";
    let i = 0;
    const id = setInterval(() => {
      i += 2;
      setTyped(full.slice(0, i));
      if (i >= full.length) {
        clearInterval(id);
        setShowRest(true);
      }
    }, 16);
    return () => clearInterval(id);
  }, [review, loading]);

  if (loading) {
    return (
      <div data-testid="ai-review-loading" className="rounded-3xl border border-emerald-500/30 bg-slate-900/70 p-8 text-center">
        <Loader2 className="w-8 h-8 text-emerald-400 animate-spin mx-auto mb-4" />
        <h3 className="font-heading text-xl font-bold text-slate-100">Analyzing your financial journey…</h3>
        <p className="text-sm text-slate-400 mt-1.5">
          Evaluating your risk tolerance, debt management, and growth velocity.
        </p>
      </div>
    );
  }
  if (!review) return null;
  const typing = !showRest;
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      data-testid="ai-review-card"
      className="rounded-3xl border border-emerald-500/30 bg-gradient-to-b from-emerald-500/[0.06] to-transparent bg-slate-900/70 p-6 sm:p-7"
    >
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4 text-emerald-400" />
        <span className="text-xs font-semibold uppercase tracking-widest text-emerald-400">Your AI Coach Review</span>
      </div>
      <h3 className="font-heading text-2xl font-bold text-slate-100" data-testid="review-persona">
        {review.persona}
      </h3>
      <p className="text-slate-300 leading-relaxed mt-3 text-[15px]" data-testid="review-summary">
        {typed}
        {typing && <span className="inline-block w-1.5 h-4 ml-0.5 bg-emerald-400 animate-pulse align-middle" />}
      </p>

      {showRest && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}>
          <div className="grid sm:grid-cols-2 gap-4 mt-6">
            <div>
              <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-emerald-400 mb-2">
                <CheckCircle2 className="w-4 h-4" /> Strengths
              </p>
              <ul className="space-y-2">
                {review.strengths?.map((s, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.15 }}
                    className="text-sm text-slate-300 flex gap-2"
                  >
                    <span className="text-emerald-400 mt-0.5">•</span>
                    <span>{s}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
            <div>
              <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-blue-400 mb-2">
                <ArrowUpRight className="w-4 h-4" /> Growth Areas
              </p>
              <ul className="space-y-2">
                {review.growth_areas?.map((s, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + i * 0.15 }}
                    className="text-sm text-slate-300 flex gap-2"
                  >
                    <span className="text-blue-400 mt-0.5">•</span>
                    <span>{s}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>

          {review.reflections?.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              data-testid="review-reflections"
              className="mt-6 rounded-2xl border border-blue-500/25 bg-blue-500/[0.06] p-4"
            >
              <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-blue-300 mb-2">
                <Scale className="w-4 h-4" /> What you said vs what you did
              </p>
              <ul className="space-y-2">
                {review.reflections.map((r, i) => (
                  <li key={i} className="text-sm text-slate-200 leading-relaxed flex gap-2">
                    <span className="text-blue-400 mt-0.5">•</span>
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}

          {review.tip && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="mt-6 rounded-2xl border border-yellow-500/30 bg-yellow-500/[0.07] p-4 flex gap-3"
            >
              <Lightbulb className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-yellow-400 mb-0.5">Coach's Tip</p>
                <p className="text-sm text-slate-200 leading-relaxed">{review.tip}</p>
              </div>
            </motion.div>
          )}
        </motion.div>
      )}
    </motion.div>
  );
};

export const Summary = ({ session, decisions, review, reviewLoading, onRestart, onSignIn }) => {
  const change = session.net_worth - (session.starting_net_worth ?? 0);
  const up = change >= 0;
  const resultsRef = useRef(null);
  const shareUrl = `${window.location.origin}/?share=${session.id}`;

  const onShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: "My Burgeon result",
          text: `I finished my Burgeon simulation with ${fmt(session.net_worth)} net worth!`,
          url: shareUrl,
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Share link copied to clipboard");
      }
    } catch (e) {
      /* user cancelled */
    }
  };

  const onDownload = async () => {
    if (!resultsRef.current) return;
    try {
      const canvas = await html2canvas(resultsRef.current, { backgroundColor: "#0B0F17", scale: 2 });
      const link = document.createElement("a");
      link.download = `burgeon-${session.nickname}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Image saved");
    } catch (e) {
      toast.error("Couldn't create image");
    }
  };

  return (
    <div className="bg-grid min-h-[calc(100vh-61px)] px-4 py-8">
      <div className="max-w-3xl mx-auto space-y-6">
        <div ref={resultsRef} className="space-y-6">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="rounded-3xl border border-slate-800 bg-slate-900/70 backdrop-blur p-6 sm:p-8 text-center"
        >
          <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/25 mb-4">
            <Trophy className="w-7 h-7 text-slate-950" />
          </div>
          <p className="text-xs font-semibold uppercase tracking-widest text-emerald-400">Simulation Complete</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-50 mt-2">
            Well played, {session.nickname}!
          </h1>
          <p className="text-slate-400 mt-2">Here's how your 8 rounds turned out.</p>

          <div className="mt-6">
            <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Final Net Worth</p>
            <p className="font-mono text-5xl font-bold text-slate-50 mt-1" data-testid="final-net-worth">
              {fmt(session.net_worth)}
            </p>
            <p className={`text-sm font-mono font-semibold mt-1 ${up ? "text-emerald-400" : "text-red-400"}`}>
              {fmtDelta(change)} from your starting {fmt(session.starting_net_worth ?? 0)}
            </p>
          </div>

          <div className="mt-6">
            <Breakdown session={session} />
          </div>
        </motion.div>

        <ReviewCard review={review} loading={reviewLoading} />
        </div>

        {!reviewLoading && <CommitmentCapture sessionId={session.id} onSignIn={onSignIn} />}

        <div className="rounded-3xl border border-slate-800 bg-slate-900/70 backdrop-blur p-6 sm:p-7">
          <h2 className="font-heading text-xl font-bold text-slate-100 mb-4">Your Decision Journey</h2>
          <Timeline decisions={decisions} />
        </div>

        <div className="flex flex-wrap justify-center gap-3 pb-6">
          <Button
            data-testid="share-result-btn"
            onClick={onShare}
            variant="outline"
            className="h-12 px-6 border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800 font-semibold rounded-xl"
          >
            <Share2 className="w-5 h-5 mr-1.5" /> Share Result
          </Button>
          <Button
            data-testid="download-result-btn"
            onClick={onDownload}
            variant="outline"
            className="h-12 px-6 border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800 font-semibold rounded-xl"
          >
            <Download className="w-5 h-5 mr-1.5" /> Save Image
          </Button>
          <Button
            data-testid="print-handout-btn"
            onClick={() => window.print()}
            variant="outline"
            className="h-12 px-6 border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800 font-semibold rounded-xl"
          >
            <Printer className="w-5 h-5 mr-1.5" /> Print Handout
          </Button>
          <Button
            data-testid="play-again-btn"
            onClick={onRestart}
            className="h-12 px-8 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-slate-950 font-bold rounded-xl shadow-lg shadow-emerald-500/25"
          >
            <RotateCcw className="w-5 h-5 mr-1" /> Play Again
          </Button>
        </div>
      </div>
      <Handout session={session} review={review} />
    </div>
  );
};
